import { Button, Card, HStack, Text, VStack, useDisclosure, useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import type React from "react";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { z } from "zod";

import { extractErrorMessage } from "@equidefi/portals";
import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { useCancelInvestment } from "@equidefi/portals/hooks/useInvestments";
import { offeringIsRule506c, offeringIsTerminated } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { BannerAlert, ConfirmModal, TooltipTime } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { SectionMiniTitle } from "@/areas/Investments/components/Sections";
import { CanEdit } from "@/components/permissions/Can";
import { canSendReminder } from "@/helpers/investments";
import { usePokeInvestor } from "@/hooks/investments";
import { useAccept } from "@/hooks/useAccreditation";

type ApprovalState =
  | "PENDING"
  | "SUBMITTED_REVIEW"
  | "SUBMITTED_APPROVAL"
  | "APPROVED"
  | "EXPEDITED"
  | "REJECTED"
  | "CANCELED";

type ApprovalStateConfig = {
  bg: string;
  borderColor: string;
  badgeBg: string;
  badgeText: string;
  message: string | null;
};

interface InvestmentApprovalCardProps {
  investment: DTO.Investment;
  onRefetch?: () => void;
}

type TCancelForm = {
  reason: string;
};

const cancelSchema = z.object({
  reason: z
    .string()
    .min(1, "Please provide a reason for cancelation")
    .min(10, "Reason must be at least 10 characters long"),
});

type StateConfigWithIcon = ApprovalStateConfig & {
  badgeIcon: React.ReactNode;
};

const APPROVAL_STATE_CONFIG: Record<ApprovalState, ApprovalStateConfig> = {
  PENDING: {
    bg: "white",
    borderColor: "gray.300",
    badgeBg: "gray.500",
    badgeText: "INVESTMENT INCOMPLETE",
    message: "Approval will be enabled once the investor has completed the investment.",
  },
  SUBMITTED_REVIEW: {
    bg: "orange.50",
    borderColor: "orange.300",
    badgeBg: "red.500",
    badgeText: "INVESTMENT READY FOR REVIEW",
    message: "Review the investors accreditation documents to enable Countersigning.",
  },
  SUBMITTED_APPROVAL: {
    bg: "orange.50",
    borderColor: "orange.300",
    badgeBg: "red.500",
    badgeText: "INVESTMENT READY FOR APPROVAL",
    message: "Approve the investment to enable Countersigning.",
  },
  APPROVED: {
    bg: "green.50",
    borderColor: "green.300",
    badgeBg: "green.500",
    badgeText: "INVESTMENT APPROVED",
    message: null,
  },
  EXPEDITED: {
    bg: "blue.50",
    borderColor: "blue.300",
    badgeBg: "blue.500",
    badgeText: "EXPEDITED INVESTMENT",
    message: null,
  },
  REJECTED: {
    bg: "red.50",
    borderColor: "red.300",
    badgeBg: "red.500",
    badgeText: "INVESTMENT REJECTED",
    message: "This investment has been rejected.",
  },
  CANCELED: {
    bg: "red.50",
    borderColor: "red.300",
    badgeBg: "red.500",
    badgeText: "INVESTMENT CANCELED",
    message: null,
  },
};

const APPROVAL_STATE_ICONS: Record<ApprovalState, React.ReactNode> = {
  PENDING: <Icon.AlertCircle size="1.2em" fontWeight="bold" />,
  SUBMITTED_REVIEW: <Icon.Clock size="1.2em" fontWeight="bold" />,
  SUBMITTED_APPROVAL: <Icon.Clock size="1.2em" fontWeight="bold" />,
  APPROVED: <Icon.CheckCircle size="1.2em" fontWeight="bold" />,
  EXPEDITED: <Icon.ChevronsRight size="1.2em" fontWeight="bold" />,
  REJECTED: <Icon.XCircle size="1.2em" fontWeight="bold" />,
  CANCELED: <Icon.XCircle size="1.2em" fontWeight="bold" />,
};

const getStateConfig = (state: ApprovalState): StateConfigWithIcon => ({
  ...APPROVAL_STATE_CONFIG[state],
  badgeIcon: APPROVAL_STATE_ICONS[state],
});

export const InvestmentApprovalCard: React.FC<InvestmentApprovalCardProps> = ({ investment, onRefetch }) => {
  const toast = useToast();
  const cancelModal = useDisclosure();
  const approveModal = useDisclosure();

  const form = useForm<TCancelForm>({
    defaultValues: { reason: "" },
    resolver: zodResolver(cancelSchema),
  });

  const vault = investment?.vault;
  const isOfferingTerminated = offeringIsTerminated(investment?.offering);

  const cancelInvestment = useCancelInvestment(investment?.id);
  const pokeInvestor = usePokeInvestor(investment?.id, {
    onSuccess: () => {
      toast({
        status: "success",
        description: `Sent a reminder to ${investment?.user?.email}`,
      });
    },
    onError: (err) => {
      toast({
        status: "error",
        description: extractErrorMessage(err, "Reminder could not be sent"),
      });
    },
  });

  const accept = useAccept(vault?.id as string);

  const handleSendReminder = async () => {
    await pokeInvestor.mutateAsync();
  };

  const handleCancel = useCallback(
    async (formData: TCancelForm) => {
      if (!investment) return;

      try {
        await cancelInvestment.mutateAsync({ id: investment.id, reason: formData.reason });

        toast({
          status: "success",
          description: "Investment was successfully canceled",
        });

        cancelModal.onClose();
        form.reset();
        onRefetch?.();
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
        toast({
          status: "error",
          description: extractErrorMessage(e, "Sorry, we could not cancel investment."),
        });
      }
    },
    [cancelInvestment, investment, cancelModal, toast, form.reset, onRefetch],
  );

  const handleApprove = async () => {
    try {
      await accept.mutateAsync({
        additional_info: true,
      });
      toast({
        status: "success",
        description: "Investment approved successfully",
      });
      approveModal.onClose();
      onRefetch?.();
    } catch (error) {
      toast({
        status: "error",
        description: "Investment could not be approved",
      });
      console.error("Approval error:", error);
    }
  };

  const is506c = offeringIsRule506c(investment?.offering);

  const approvalState = useMemo((): ApprovalState => {
    if (investment?.status === "CANCELED") return "CANCELED";
    if (investment?.status === "REJECTED") return "REJECTED";

    const vaultStatus = vault?.status;

    if (vaultStatus === "APPROVED") {
      // Expedited investment applies only to Reg D 506c offerings
      if (investment?.expedited && is506c) {
        return "EXPEDITED";
      }
      return "APPROVED";
    }

    if (vaultStatus === "SUBMITTED") {
      // For Reg D 506c, show "Ready for Review" (redirect to accreditation tab)
      // For other offerings (Reg A, Reg D 506b), show "Ready for Approval" (direct approve action)
      return is506c ? "SUBMITTED_REVIEW" : "SUBMITTED_APPROVAL";
    }

    // vault.status === "REJECTED" means only accreditation was rejected, not the investment itself.
    // The investor must resubmit, so the investment is still incomplete.
    return "PENDING";
  }, [vault?.status, investment?.status, investment?.expedited, is506c]);

  const stateConfig = useMemo(() => getStateConfig(approvalState), [approvalState]);

  const hideCancelButton =
    ["CLOSED", "REJECTED", "CANCELED"].includes(investment?.status ?? "") ||
    isOfferingTerminated ||
    !!investment?.offering_tranche_id;
  // Reminder stays available until every countersignature check is clear (ready to countersign),
  // independent of the accreditation approval state shown on the card.
  const showReminder = canSendReminder(investment);

  // Rendered inline with the other actions in the pre-approval branch, and attached to the
  // "Approved" block once accreditation is approved but signing/payment is still outstanding.
  const reminderButton = (
    <Button
      variant="link"
      colorScheme="blue"
      onClick={handleSendReminder}
      isLoading={pokeInvestor.isLoading}
      fontWeight="normal"
      fontSize="md">
      Send Reminder
    </Button>
  );

  if (!investment) return null;

  return (
    <Card
      bg={stateConfig.bg}
      border="1px solid"
      borderColor={stateConfig.borderColor}
      borderRadius="lg"
      p={5}
      boxShadow="sm">
      <HStack
        bg={stateConfig.badgeBg}
        color="white"
        py={1.5}
        px={3}
        borderRadius="md"
        spacing={2}
        mb={4}
        alignSelf="flex-start">
        {stateConfig.badgeIcon}
        <SectionMiniTitle color="white" my={0.5} px={0}>
          {stateConfig.badgeText}
        </SectionMiniTitle>
      </HStack>

      {approvalState === "APPROVED" || approvalState === "EXPEDITED" ? (
        <VStack align="start" spacing={2}>
          <Text color="gray.800" fontSize="md" mb={0}>
            Approved{" "}
            <TooltipTime datetime={vault?.approved_date || vault?.modify_date} tooltip="llll" format="MMM Do, YYYY" />
            {vault?.approver?.full_name && ` by ${vault.approver.full_name}`}.
          </Text>
          <CanEdit userRole="owner" offeringId={investment?.offering?.id}>
            <HStack spacing={6} align="center">
              {showReminder && reminderButton}
              {!hideCancelButton && (
                <Button variant="link" onClick={cancelModal.onOpen} colorScheme="red" fontWeight="normal" fontSize="md">
                  Cancel Investment
                </Button>
              )}
            </HStack>
          </CanEdit>
        </VStack>
      ) : approvalState === "CANCELED" ? (
        <VStack align="start" spacing={2}>
          <Text color="gray.800" fontSize="md" mb={0}>
            Canceled <TooltipTime datetime={investment?.modify_date} tooltip="llll" format="MMM Do, YYYY" />.
          </Text>
        </VStack>
      ) : (
        <>
          {stateConfig.message && (
            <Text color="gray.600" fontSize="md" mb={4} lineHeight="1.6">
              {stateConfig.message}
            </Text>
          )}

          <CanEdit userRole="owner" offeringId={investment?.offering?.id}>
            <HStack spacing={6} align="center">
              {approvalState === "SUBMITTED_REVIEW" && (
                <Button
                  as={Link}
                  to={`/offerings/${investment.offering_id}/investments/${investment.id}/accreditation`}
                  colorScheme="purple"
                  size="md">
                  Review
                </Button>
              )}
              {approvalState === "SUBMITTED_APPROVAL" && (
                <Button colorScheme="purple" onClick={approveModal.onOpen} isLoading={accept.isLoading} size="md">
                  Approve
                </Button>
              )}
              {showReminder && reminderButton}
              {!hideCancelButton && (
                <Button variant="link" onClick={cancelModal.onOpen} colorScheme="red" fontWeight="normal" fontSize="md">
                  Cancel Investment
                </Button>
              )}
            </HStack>
          </CanEdit>
        </>
      )}

      <ConfirmModal
        isOpen={approveModal.isOpen}
        title="Approve Investment"
        description="Are you sure you want to approve this investment? This will enable countersigning."
        confirmText="Approve"
        onConfirm={handleApprove}
        onCancel={approveModal.onClose}
        confirmButtonProps={{ isLoading: accept.isLoading }}
      />

      <SmartFormProvider form={form}>
        {({ Textarea }) => (
          <ConfirmModal
            onConfirm={form.handleSubmit(handleCancel)}
            onCancel={() => {
              cancelModal.onClose();
              form.reset();
            }}
            isOpen={cancelModal.isOpen}
            confirmText="Cancel Investment"
            confirmButtonColor="red"
            confirmButtonProps={{
              isLoading: cancelInvestment.isLoading,
            }}
            title="Are you sure you want to cancel this investment?">
            <BannerAlert
              status="warning"
              mb="6"
              title="Before you cancel..."
              description={
                <>
                  <Text as="ul" pl="4" mb="0" listStyleType="disc" listStylePos="outside">
                    <Text as="li">Any pending payments should be canceled.</Text>
                    <Text as="li">Any successful payments need to be refunded.</Text>
                    <Text as="li" mb="0">
                      Leave a note on the investment clarifying the reason for canceling this investment
                    </Text>
                  </Text>
                  <Text mt="4" mb="0" fontWeight="bold">
                    This reason will be emailed to the investor and all signers.
                  </Text>
                </>
              }
            />
            <Textarea
              name="reason"
              label="Please provide a short explanation for why this investment is being canceled"
              placeholder="Enter cancelation reason..."
              isRequired
              rows={3}
            />
          </ConfirmModal>
        )}
      </SmartFormProvider>
    </Card>
  );
};
